<template>
	<div class="d-flex w-100 align-start pa-0">
		<v-menu
			v-model="showMenu"
			class="w-100"
			:close-on-content-click="closeOnContentClick"
			:disabled="disabled"
		>
			<template #activator="{ props }">
				<div
					v-bind="props"
					class="w-100"
				>
					<v-select
						v-bind="selectProps"
						:model-value="modelValue"
						readonly
						color="primary"
						height="24"
						menu-icon="mdi-chevron-down"
						:class="[
							customClass,
							'ao-select',
							!excludePadding ? 'tiny' : '',
							showMenu && 'v-select--active-menu',
						]"
						:items="displayedItems"
						:loading="loading"
						:error="error"
						:error-messages="errorMessages"
						:messages="messages"
						:disabled="disabled"
						@update:modelValue="onUpdateModelValue"
						@update:focused="onFocusedChange"
						@click:clear="onClickClear"
					>
						<template v-slot:selection="{ item }">
							<div class="d-flex align-center">
								<ao-icon
									v-if="prependIcon"
									:iconName="prependIcon"
								/>
								<span
									class="ml-1 ao-select-selected-item"
									:class="{ chips: chips }"
								>
									{{ item.title || item.value }}
									<slot name="selection" />
								</span>
							</div>
						</template>
						<template #chip="{ item }">
							<v-chip
								v-if="chips"
								:closable="closableChips"
								@click:close.stop="onClickCloseChip(item.raw)"
							>
								{{ item.title || item.value }}
							</v-chip>
							<template
								v-else
							>
								{{ item.title || item.value }}
							</template>
						</template>
					</v-select>
				</div>
			</template>

			<!-- Я закоментировал эту строку потому что она делала дропдаун селекта сильно шире основного инпута -->
			<!-- :width="menuWidth" -->
			<v-card
				class="px-0 pa-1 pt-3"
				:style="menuMaxWidth ? `max-width: ${menuMaxWidth}px` : ''"
			>
				<template v-if="isSearch || multiselect || isFullItemsAvailable">
					<div
						class="w-100 d-flex align-items-center"
						@click.stop
					>
						<ao-text-field
							v-if="isSearch"
							v-model="query"
							hide-details
							autofocus
							class="mx-3 mt-n4 mb-2"
							color="primary"
							:placeholder="$t('Search')"
							density="compact"
							clear-icon="mdi-close"
							variant="underlined"
						/>

						<div
							v-if="multiselect"
							class="ao-select-fast-selection"
							:class="{ 'ml-auto': !isSearch }"
						>
							<v-tooltip
								:text="isAllItemsSelected ? $t('Clear selection') : $t('Select all')"
								location="bottom"
							>
								<template v-slot:activator="{ props }">
									<ao-checkbox
										v-bind="props"
										:model-value="isAllItemsSelected"
										class="checkbox"
										fit
										density="compact"
										hide-details
										label=""
										style="background: white"
										hint=""
										@click.stop
										@update:modelValue="isAllItemsSelected ? deselectAll() : selectAll()"
									/>
								</template>
							</v-tooltip>
						</div>

						<ao-icon-btn
							v-if="isFullItemsAvailable"
							class="mr-3"
							size="small"
							:iconName="showFullList ? 'collapseClose' : 'collapseOpen'"
							@click="showFullList = !showFullList"
						/>
					</div>
				</template>

				<template
					v-if="loading"
				>
					<ao-preloader
						:width="2"
						:size="32"
						:paddingY="6"
					/>
				</template>

				<v-virtual-scroll
					v-else
					:key="getItems.length"
					:items="getItems"
					:max-height="180"
					:visible-items="12"
				>
					<template v-slot:default="{ item }">
						<slot
							v-if="hasItemSlot"
							name="item"
							:item="{ item }"
						/>
						<v-list-item
							density="compact"
							height="30"
							max-height="30"
							min-height="30"
							:class="[
								multiselect && 'multiple',
								isSelected(item) && 'active',
								'ao-select-list-item',
							]"
							@click.stop="handleSelect(item)"
						>
							<template #title>
								<span class="ao-c1">{{ Array.isArray(itemTitle) ? itemTitle.map(v => item[v]).join(' ') : typeof item === 'object' ? item[itemTitle] : item }}</span>
							</template>
						</v-list-item>
					</template>
				</v-virtual-scroll>
			</v-card>
		</v-menu>

		<slot name="action">
			<ao-icon-btn
				v-if="showAddButton"
				class="ml-3 mt-3"
				color="primary"
				iconName="plus"
				@click="onClickPlus"
			/>
		</slot>
	</div>
</template>

<script>
export default {
	name: 'AoSelect',

	components: {},

	props: {
		modelValue: {
			type: [Array, Number, String],
			default: null,
		},
		label: {
			type: String,
			default: '',
		},
		items: {
			type: Array,
			default: () => [],
		},
		itemsFull: {
			type: Array,
			default: () => [],
		},
		itemTitle: {
			type: String,
			default: 'title',
		},
		itemValue: {
			type: String,
			default: 'value',
		},
		custom: {
			type: String,
			default: null,
			// TODO: add validation
		},
		variant: {
			type: String,
			default: 'underlined',
		},
		excludePadding: Boolean,

		loading: {
			type: [Boolean, String],
		},

		color: {
			type: String,
			default: undefined,
		},

		density: {
			type: String,
			default: 'default',
		},

		hideDetails: {
			type: [Boolean, String],
		},

		error: {
			type: Boolean,
			default: false,
		},
		errorMessages: {
			type: Array,
			default: () => [],
		},
		multiselect: Boolean,

		clearable: {
			type: Boolean,
			default: false,
		},

		chips: Boolean,
		closableChips: Boolean,

		prependIcon: {
			type: String,
		},

		isSearch: Boolean,

		showAddButton: Boolean,
		disabled: {
			type: Boolean,
			default: false,
		},

		menuWidth: {
			type: Number,
			default: () => 380,
		},

		menuMaxWidth: {
			type: Number,
		},

		messages: {
			type: String
		},

		isSorted: {
			type: Boolean,
			default: true,
		},
		sortCompareCallback: {
			type: Function,
			default: null,
		},
		openOnMounted: {
			type: Boolean,
			default: false,
		},
	},

	emits: ['update:modelValue', 'onClickPlus', 'focus', 'blur', 'list:closed'],

	data() {
		return {
			query: '',
			showMenu: false,
			showFullList: false,
			wasOpened: false,
		}
	},

	computed: {
		fullItemsWithoutItems() {
			return this.itemsFull?.filter(item => {
				if (typeof item === 'object') {
					return !this.itemsWithSelected.map(item => item[this.itemValue]).includes(item[this.itemValue])
				}

				return !this.itemsWithSelected.includes(item)
			})
		},
		itemsWithSelected() {
			return (!this.isSelectedIncludedByItems ? [...(this.multiselect ? this.selectedItem || [] : (this.selectedItem && [this.selectedItem] || [])),...this.items] : this.items)
				.reduce((acc, cur) => {
					if (typeof cur === 'object' && !acc.map(item => item[this.itemValue]).includes(cur[this.itemValue])) {
						acc.push(cur)
					} else if (!acc.includes(cur)) {
						acc.push(cur)
					}

					return acc
				}, [])
		},
		displayedItems() {
			return (this.itemsFull?.length && this.showFullList)
				? [
					...this.itemsWithSelected,
					...this.fullItemsWithoutItems
				]
				: this.itemsWithSelected
		},
		isFullItemsAvailable() {
			return this.itemsFull?.length > 0
		},
		isSelectedIncludedByItems() {
			if (this.multiselect) {
				const itemsValues = this.items.map(item => typeof item === 'object' ? item[this.itemValue] : item)

				return (Array.isArray(this.modelValue) ? this.modelValue : []).every(item => itemsValues.includes(item))
			} else {
				return this.items
					.map(item => typeof item === 'object' ? item[this.itemValue] : item)
					.includes(this.modelValue)
			}
		},
		selectedItem() {
			if (this.multiselect) {
				return (this.itemsFull?.length ? this.itemsFull : this.items).filter(item => {
						if (typeof item === 'object') {
							return this.modelValue.includes(item[this.itemValue])
						}

						return this.modelValue.includes(item)
					})
			} else {
				return (this.itemsFull?.length ? this.itemsFull : this.items).find(item => {
					if (typeof item === 'object') {
						return item[this.itemValue] === this.modelValue
					}

					return item === this.modelValue
				})
			}
		},
		isAllItemsSelected() {
			if (this.itemsFull?.length && this.multiselect) {
				return this.itemsFull.length === (this.modelValue || []).length
			} else if (this.multiselect) {
				return this.items.length === (this.modelValue || []).length
			}

			return false
		},
		selectProps() {
			const props = {}
			if (this.color) props.color = this.color
			if (this.itemTitle) props.itemTitle = this.getTitle(this.itemTitle)
			if (this.itemValue) props.itemValue = this.itemValue
			if (this.density) props.density = this.density
			if (this.variant) props.variant = this.variant
			if (this.label) props.label = this.label
			if (this.hideDetails) props.hideDetails = this.hideDetails
			if (this.multiselect) props.multiselect = this.multiselect
			if (this.clearable) props.clearable = this.clearable
			if (this.chips) props.chips = this.chips
			if (this.closableChips) props.closableChips = this.closableChips

			return props
		},
		customClass() {
			let prefix = 'ao-custom-text-field-'
			let customClass = ''
			if (this.custom) {
				customClass = prefix + this.custom
			}
			return customClass
		},
		hasItemSlot() {
			return !!this.$slots.item
		},
		foundItems() {
			if (!this.query) {
				return this.displayedItems
			}

			const filteredItems = (this.itemsFull.length ? this.itemsFull : this.items).filter((item) => {
				// let key = Array.isArray(this.itemTitle) ? item.editedItemTitle : (typeof item === 'object' ?  item[this.itemTitle] : item)
				let key = typeof item === 'object' ?  item[this.itemTitle] : item
				let isCurrentKeyboardLayoutResult = `${key}`
					.toLowerCase()
					.includes(this.query.trim().toLowerCase())
				if (isCurrentKeyboardLayoutResult) {
					return true
				} else {
					// 	// console.log(">>",this.getTitle(this.itemTitle)
					// 	// 	.toLowerCase()
					// 	// 	.includes(getAlternateKeyboardLayout(this.query).toLowerCase()))
					// 	return false
				}
				return false
			})

			return filteredItems
		},
		getItems() {
			let sortedItems = [...this.foundItems]
			if (this.isSorted) {
				const compareCallback = this.sortCompareCallback || ((a, b) => {
					if (typeof a === 'object' && typeof b === 'object') {
						if (a[this.itemTitle] === b[this.itemTitle]) return 0
						return a[this.itemTitle] > b[this.itemTitle] ? 1 : -1
					} else {
						if (a === b) return 0
						return a > b ? 1 : -1
					}
				})
				sortedItems.sort(compareCallback)
			}

			return sortedItems
		},
		closeOnContentClick() {
			return !(this.multiselect || this.isSearch)
		},
	},

	watch: {
		showMenu: {
			handler(value) {
				if (!value) {
					this.query = ''
					this.showFullList = false
					this.$emit('list:closed')
				}

				if (!this.wasOpened) {
					setTimeout(() => window.dispatchEvent(new Event('resize')), 0)
					this.wasOpened = true
				}
			}
		}
	},
	created() {
		if (Array.isArray(this.itemTitle)) {
			try {
				throw new Error('itemTitle must be string')
			} catch (e) {
				// eslint-disable-next-line no-console
				console.error(e)
			}
			// this.items.forEach((item) => {
			// 	let result = ''
			// 	for (let key of this.itemTitle) {
			// 		if (item[key]) {
			// 			result += item[key]
			// 				? `${
			// 						key === 'position' || key === 'registration_number'
			// 							? `(${item[key]})`
			// 							: `${item[key]}`
			// 				  }`
			// 				: ''
			// 		}
			// 	}
			// 	item.editedItemTitle = result.trim()
			// })
		}
		if (this.openOnMounted) {
			this.openList()
		}
	},

	expose: [
		'openList',
		'closeList',
	],

	methods: {
		openList() {
			this.showMenu = true
		},
		closeList() {
			this.showMenu = false
		},
		onFocusedChange(value) {
			if (value) {
				this.$emit('focus')
			} else {
				this.$emit('blur')
			}
		},
		onUpdateModelValue(event) {
			if (this.multiselect) {
				this.$emit('update:modelValue', this.modelValue.filter(v => v !== event))
			} else {
				this.$emit('update:modelValue', this.modelValue)
			}
		},
		onClickCloseChip(item) {
			const itemValue = typeof item === 'object' ? item[this.itemValue] : item
			this.onUpdateModelValue(itemValue)
		},
		onClickClear() {
			this.$emit('update:modelValue', this.multiselect ? [] : null)
		},
		handleSelect(item) {
			const itemValue = typeof item === 'object' ? item[this.itemValue] : item

			if (this.multiselect) {
				const selected = Array.isArray(this.modelValue) ? this.modelValue : []
				const idx = selected.findIndex((i) => i === itemValue)
				if (idx > -1) {
					selected.splice(idx, 1)
				} else {
					selected.push(itemValue)
				}
				this.$emit('update:modelValue', selected)
			} else {
				this.showMenu = false
				this.$emit('update:modelValue', itemValue)
			}
		},
		getKey(item) {
			return item[this.itemValue] ? item[this.itemValue] : item
		},

		getTitle(item) {
			// if (Array.isArray(this.itemTitle)) {
			// 	return 'editedItemTitle'
			// }

			if (item[this.itemTitle] === '') return item[this.itemTitle]

			return item[this.itemTitle] ? this.itemTitle : item
		},
		onClickPlus() {
			this.$emit('onClickPlus')
		},
		isSelected(item) {
			if (this.multiselect && Array.isArray(this.modelValue)) {
				return this.modelValue.includes(typeof item === 'object' ? item[this.itemValue] : item)
			} else if (this.modelValue) {
				return this.modelValue === (typeof item === 'object' ? item[this.itemValue] : item)
			}

			return false
		},
		selectAll() {
			const mapFunc = (item) => this.itemValue ? item[this.itemValue] : item
			const allItemsValues = this.itemsFull.length ? this.itemsFull.map(mapFunc) : this.items.map(mapFunc)

			this.$emit('update:modelValue', allItemsValues)
		},
		deselectAll() {
			this.$emit('update:modelValue', [])
		},
	},
}
</script>

<style lang="scss">
.tiny.ao-select.v-text-field {
	padding-top: 0;
	margin-top: 0;
}
.ao-select-list-item {
	padding-inline-start: 12px !important;
	margin: 0 8px 4px 8px;
	border-radius: 4px;

	&:last-child {
		margin-bottom: 0;
	}
}
.ao-select {
	width: 100%;

	.v-chip {
		margin: 0;
	}

	.v-input__control {
		display: block;
	}

	.v-field__input {
		font-size: 14px;
		line-height: 1.2;
		padding-top: 16px;
	}
	.v-field__append-inner {
		align-items: center;
	}
	.ao-select-selected-item {
		&.chips {
			background: #f0f4f7 !important;
			padding: 4px;
			border-radius: 20px;
			margin: 2px 0;
		}
	}
	// .v-label.v-field-label {
	// 	// font-size: 12px;
	// }
}
.v-virtual-scroll {
	.v-list-item.multiple {
		padding-left: 34px !important;
		background: transparent !important;

		.v-list-item__overlay {
			background: transparent;
		}

		.v-list-item__prepend {
			display: none;
		}
	}

	.v-list-item.multiple.v-list-item--active:after {
		content: url('@/assets/icons/Check.svg');

		padding-top: 3px;

		border: none;
		background: rgb(var(--v-theme-primary)) !important;
	}

	// .v-list-item.multiple.active:after {
	// 	content: url('@/assets/icons/Check.svg');

	// 	padding-top: 3px;

	// 	border: none;
	// 	background: rgb(var(--v-theme-primary)) !important;
	// }

	.v-list-item {

		&.active {
			background-color: rgba(var(--v-theme-primary), 0.25) !important;
		}

	}

	.v-list-item.multiple {

		.v-list-item-title:after {
			content: '';

			position: absolute;
			top: 50%;
			left: 12px;
			transform: translateY(-50%);

			display: flex;
			justify-content: center;
			align-items: center;

			width: 16px;
			height: 16px;

			border: 1px solid #86919d;
			border-radius: 3px;
			background: transparent;
		}

		&.active {

			.v-list-item-title:after {
				content: url('@/assets/icons/Check.svg');

				padding-top: 3px;

				border: none;
				background: rgb(var(--v-theme-primary)) !important;
			}

		}

	}
}

.ao-select-fast-selection {
	display: flex;
	align-self: center;
	min-width: 20px;
}
</style>
